import { Timestamp } from 'firebase/firestore';
import { differenceInDays } from 'date-fns';
import type { Plant, AnyCareInfo, WaterStatus } from '../types/plant';
import type { ClimateZone, Hemisphere } from '../types/user';
import { isExpandedCareInfo } from '../types/careExpanded';
import { getCareInfoText } from '../types/plant';

export type Season = 'spring' | 'summer' | 'autumn' | 'winter';

function clampInterval(days: number): number {
  return Math.max(1, Math.min(90, Math.round(days)));
}

/**
 * Parse a numeric watering interval (in days) from care info text.
 * Returns null if unparseable. Result is clamped to 1–90.
 */
export function parseWateringInterval(careInfo: AnyCareInfo | null): number | null {
  if (!careInfo) return null;

  // Check v2 intervalDays first
  if (isExpandedCareInfo(careInfo)) {
    const interval = (careInfo.watering as { intervalDays?: number })?.intervalDays;
    if (typeof interval === 'number' && interval > 0) return clampInterval(interval);
  }

  // Extract frequency text from either v1 or v2
  let frequencyText: string | null = null;
  if (isExpandedCareInfo(careInfo)) {
    frequencyText = careInfo.watering?.frequency ?? null;
  } else {
    frequencyText = getCareInfoText(careInfo.watering);
  }

  if (!frequencyText) return null;
  const text = frequencyText.toLowerCase();

  // "daily" or "every day"
  if (/\bdaily\b/.test(text) || /\bevery\s+day\b/.test(text)) return 1;

  // "every X days" or "every X-Y days" (take average)
  const everyXDays = text.match(/every\s+(\d+)\s*(?:-\s*(\d+))?\s*days/);
  if (everyXDays) {
    const low = parseInt(everyXDays[1]!, 10);
    const high = everyXDays[2] ? parseInt(everyXDays[2], 10) : low;
    return clampInterval(Math.round((low + high) / 2));
  }

  // "once a week" / "weekly" / "every week"
  if (/\bweekly\b/.test(text) || /\bonce\s+a\s+week\b/.test(text) || /\bevery\s+week\b/.test(text)) return 7;

  // "twice a week" / "2x a week"
  if (/\btwice\s+a\s+week\b/.test(text) || /\b2x?\s+a\s+week\b/.test(text)) return 3;

  // "every X weeks"
  const everyXWeeks = text.match(/every\s+(\d+)\s*weeks?/);
  if (everyXWeeks) return clampInterval(parseInt(everyXWeeks[1]!, 10) * 7);

  // "biweekly" / "every two weeks" / "every 2 weeks"
  if (/\bbi-?weekly\b/.test(text) || /\bevery\s+two\s+weeks\b/.test(text)) return 14;

  // "monthly" / "once a month" / "every month"
  if (/\bmonthly\b/.test(text) || /\bonce\s+a\s+month\b/.test(text) || /\bevery\s+month\b/.test(text)) return 30;

  // "every X months"
  const everyXMonths = text.match(/every\s+(\d+)\s*months?/);
  if (everyXMonths) return clampInterval(parseInt(everyXMonths[1]!, 10) * 30);

  // "when top X inch(es) dry" or "when soil/top ... dry" → default 10
  if (/when\s+top\s+\d+/.test(text) || /when\s+(soil|top)\b.*\bdry\b/.test(text)) return 10;

  return null;
}

/**
 * Determine the current season based on date and hemisphere.
 */
export function getCurrentSeason(date: Date, hemisphere: Hemisphere | null): Season {
  const month = date.getMonth(); // 0-11
  // Northern hemisphere seasons
  let season: Season;
  if (month >= 2 && month <= 4) season = 'spring';
  else if (month >= 5 && month <= 7) season = 'summer';
  else if (month >= 8 && month <= 10) season = 'autumn';
  else season = 'winter';

  // Invert for southern hemisphere
  if (hemisphere === 'south') {
    const invert: Record<Season, Season> = {
      spring: 'autumn',
      summer: 'winter',
      autumn: 'spring',
      winter: 'summer',
    };
    season = invert[season];
  }

  return season;
}

/**
 * Seasonal multiplier lookup table.
 * >1 means water less frequently (longer interval), <1 means more frequently.
 */
const SEASONAL_MULTIPLIERS: Record<ClimateZone, Record<Season, number>> = {
  tropical:     { spring: 1.0, summer: 0.9, autumn: 1.0, winter: 1.1 },
  arid:         { spring: 0.8, summer: 0.7, autumn: 0.9, winter: 1.3 },
  subtropical:  { spring: 0.9, summer: 0.8, autumn: 1.0, winter: 1.2 },
  temperate:    { spring: 0.9, summer: 0.8, autumn: 1.1, winter: 1.4 },
  continental:  { spring: 0.85, summer: 0.75, autumn: 1.1, winter: 1.5 },
  subarctic:    { spring: 0.9, summer: 0.8, autumn: 1.2, winter: 1.6 },
};

export function getSeasonalMultiplier(season: Season, climateZone: ClimateZone | null): number {
  if (!climateZone) return 1.0;
  return SEASONAL_MULTIPLIERS[climateZone]?.[season] ?? 1.0;
}

/**
 * Compute the effective watering interval for a plant, accounting for
 * manual adjustment and seasonal multiplier.
 * Returns null if no base interval is known.
 */
export function getEffectiveInterval(
  plant: Plant,
  climateZone: ClimateZone | null,
  hemisphere: Hemisphere | null,
  now?: Date,
): number | null {
  const base = plant.wateringIntervalDays;
  if (base == null) return null;

  const adjustment = plant.wateringAdjustmentDays ?? 0;
  const adjustedBase = base + adjustment;

  if (!plant.autoAdjust || !climateZone) {
    return Math.max(1, Math.round(adjustedBase));
  }

  const season = getCurrentSeason(now ?? new Date(), hemisphere);
  const multiplier = getSeasonalMultiplier(season, climateZone);
  return Math.max(1, Math.round(adjustedBase * multiplier));
}

/**
 * Smart water status using effective interval.
 * ≤60% of interval = fresh, ≤100% = due-soon, >100% = overdue.
 */
export function getSmartWaterStatus(
  lastWatered: Timestamp | null,
  effectiveInterval: number,
): WaterStatus {
  if (!lastWatered) return 'never-watered';

  const daysSince = differenceInDays(new Date(), lastWatered.toDate());
  const ratio = daysSince / effectiveInterval;

  if (ratio <= 0.6) return 'fresh';
  if (ratio <= 1.0) return 'due-soon';
  return 'overdue';
}

/**
 * Days until next watering. Negative means overdue.
 * Returns null if interval is unknown.
 */
export function getDaysUntilWatering(
  lastWatered: Timestamp | null,
  effectiveInterval: number | null,
): number | null {
  if (effectiveInterval == null || !lastWatered) return null;
  const daysSince = differenceInDays(new Date(), lastWatered.toDate());
  return effectiveInterval - daysSince;
}
